'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import GradientButton from '@/components/button-gradient-epic'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { API_URL, NETWORK } from '@/utils/constants'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import Link from 'next/link'
import { CirclePlus, CircleCheck, ChevronDown } from 'lucide-react'
import { Slider } from '@/components/ui/slider'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import BitcoinIcon from '@/icons/bitcoin-filled'

import EditIcon from '@/icons/edit'

import Event from '@/utils/event'

import useDebounce from '@/utils/hooks/useDebounce'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const _LIMIT = 100
const _CONSOLIDATION_LIMIT = 2000

const UtxosCart: FC<{
	handleModalClose: () => void
}> = (props): ReactElement => {
	const { data, isLoading } = useSwr(
		`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`,
		fetcher
	)

	const { handleModalClose } = props

	const [step, setStep] = React.useState(1)
	const [selected, setSelected] = React.useState(new Set())
	const [numOutputs, setOutputs] = React.useState(2)
	const [size, setSize] = React.useState<any>(null)
	const [feeRate, setFeeRate] = React.useState(0)
	const [fee, setFee] = React.useState(0)
	const [loading, setLoading] = React.useState(false)
	const [slider, setSlider] = React.useState(0)
	const [sortOrder, setSortOrder] = React.useState<'biggest' | 'smallest'>('smallest')
	const [sortOpen, setSortOpen] = React.useState(false)
	const sortRef = React.useRef<HTMLDivElement>(null)
	const debouncedNumOuputs: number = useDebounce<number>(numOutputs, 300)
	const debouncedFeeRate: number = useDebounce<number>(feeRate, 300)
	const { exchangeRate } = useExchangeRate()
	const [sendToAddress, setSendToAddress] = React.useState('')

	const { data: feeData } = useFeeEstimates()

	// Initialize fee rate from fee estimates
	React.useEffect(() => {
		if (feeData?.hourFee) {
			const minFee = feeData?.minimumFee || 5
			requestAnimationFrame(() => {
				setFeeRate(feeData.hourFee < minFee ? minFee : parseInt(feeData.hourFee.toFixed(0), 10))
			})
		}
	}, [feeData])

	const feeMin = feeData?.minimumFee || 5
	const feeMax = feeData?.fastestFee ? parseInt(feeData.fastestFee.toFixed(0), 10) * 2 : 500
	const feeSpeed =
		feeRate <= (feeData?.economyFee || 10)
			? 'Slow'
			: feeRate <= (feeData?.halfHourFee || 30)
			? 'Medium'
			: 'Fast'

	const utxos = React.useMemo(() => {
		return (data?.utxos || [])
			?.sort((a: any, b: any) => (sortOrder === 'biggest' ? b.value - a.value : a.value - b.value))
			.map((e: any) => ({ ...e, outpoint: `${e.txid}:${e.vout}` }))
	}, [data, sortOrder])

	const { selectedUtxos, inputSats } = React.useMemo(() => {
		const selectedUtxos = Array.from(selected?.values() || []).map((e) =>
			utxos.find((u: any) => u.outpoint === e)
		)
		const inputSats = selectedUtxos.reduce((a, e) => e.value + a, 0)

		return { selectedUtxos, inputSats }
	}, [selected, utxos])

	const handleSelect = React.useCallback(
		(outpoint: any) => {
			if (selected.has(outpoint)) {
				const newSet = new Set(selected)
				newSet.delete(outpoint)
				setSelected(newSet)
				setSlider(newSet.size)
			} else {
				const newSet = new Set(selected).add(outpoint)
				setSelected(newSet)
				setSlider(newSet.size)
			}
		},
		[selected]
	)

	const onSliderChange = React.useCallback(
		([v]: any) => {
			setSlider(v)
			const newSet = new Set<string>()
			for (let i = 0; i < v && i < utxos.length; i++) {
				newSet.add(utxos[i].outpoint)
			}
			setSelected(newSet)
		},
		[utxos]
	)

	const handleSortChange = React.useCallback((order: 'biggest' | 'smallest') => {
		setSortOrder(order)
		setSortOpen(false)
	}, [])

	// Close sort dropdown on outside click
	React.useEffect(() => {
		if (!sortOpen) return
		const handler = (e: MouseEvent) => {
			if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
				setSortOpen(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [sortOpen])

	// Re-select from top when sort order changes
	React.useEffect(() => {
		if (slider > 0 && utxos.length > 0) {
			const newSet = new Set<string>()
			for (let i = 0; i < slider && i < utxos.length; i++) {
				newSet.add(utxos[i].outpoint)
			}
			requestAnimationFrame(() => {
				setSelected(newSet)
			})
		}
	}, [slider, sortOrder, utxos])

	React.useEffect(() => {
		if (!selectedUtxos.length && utxos.length > 0) {
			const utxo = utxos[0]
			if (utxo) {
				requestAnimationFrame(() => {
					setSelected(new Set().add(utxo.outpoint))
					setSlider(1)
				})
			}
		}
	}, [selectedUtxos.length, utxos])

	// Fee estimation for UI preview (original working pattern)
	React.useEffect(() => {
		async function run() {
			setLoading(true)
			try {
				if (!selectedUtxos.length) {
					return
				}

				// @ts-ignore
				const outputs = new Array(parseInt(debouncedNumOuputs, 10))
					.fill('')
					.map((_e) => [Wallet.balanceAddress(), NETWORK === 'BTC' ? 546 : 1e6])

				const payload = {
					outputs: outputs,
					public_key: Wallet.getPublicKey(),
					from: Wallet.balanceAddress(),
					utxos: selectedUtxos.map((e) => [e.txid, e.vout, e.value]),
					fee_rate: debouncedFeeRate,
					purpose: 'utxo_split'
				}

				const { data } = await axios.post(`${API_URL}/wallet/build`, payload)

				const value = parseInt(
					Math.floor((inputSats - data.fees) / debouncedNumOuputs).toFixed(0),
					10
				)

				setFee(data.fees)
				setSize(value as any)
			} catch (e) {
				console.log(e)
			}

			setLoading(false)
		}

		run()
	}, [debouncedNumOuputs, debouncedFeeRate, selectedUtxos, inputSats])

	const [submitting, setSubmitting] = React.useState(false)

	// Build, sign, and broadcast on submit
	const handleSubmit = React.useCallback(async () => {
		setSubmitting(true)
		try {
			const outputs = new Array(numOutputs)
				.fill('')
				.map(() => [sendToAddress || Wallet.balanceAddress(), NETWORK === 'BTC' ? 546 : 1e6])

			const payload = {
				outputs,
				public_key: Wallet.getPublicKey(),
				from: Wallet.balanceAddress(),
				utxos: selectedUtxos.map((e) => [e.txid, e.vout, e.value]),
				fee_rate: feeRate,
				purpose: 'utxo_split'
			}

			const { data } = await axios.post(`${API_URL}/wallet/build`, payload)

			const { rawtx } = await Wallet.signInscribe(data.psbt)
			handleModalClose()

			Event.emit('open-broadcast-modal', {
				type: 'send',
				address: sendToAddress || Wallet.balanceAddress(),
				rawtx,
				psbt: data.psbt
			})
		} catch (e) {
			Wallet.handleError(e)
		} finally {
			setSubmitting(false)
		}
	}, [sendToAddress, numOutputs, feeRate, handleModalClose, selectedUtxos])

	const handleChange = React.useCallback(async (evt: any) => {
		setSendToAddress(evt.target.value)
	}, [])

	const handleOutputsInput = React.useCallback((evt: any) => {
		const val = parseInt(evt.target.value, 10)
		if (isNaN(val) || val < 1) {
			setOutputs(1)
		} else {
			setOutputs(val)
		}
	}, [])

	const handleFeeInput = React.useCallback(
		(evt: any) => {
			const val = parseInt(evt.target.value, 10)
			if (isNaN(val)) return
			setFeeRate(Math.max(feeMin, Math.min(val, 1000)))
		},
		[feeMin]
	)

	if (step === 1) {
		return (
			<div className="w-full max-w-[400px] flex flex-col gap-3">
				<div>
					<h2 className="text-base font-semibold text-foreground">Select UTXOs to split</h2>
					<p className="text-xs text-muted-foreground mt-1">
						Split large UTXOs into smaller ones to make multiple purchases at once, or consolidate
						small UTXOs into fewer, larger ones.
					</p>
				</div>

				{utxos.length > 0 && (
					<div className="flex items-center gap-2">
						<span className="text-[10px] text-muted-foreground shrink-0">
							{selected.size}/{utxos.length}
						</span>
						<div className="flex-1">
							<Slider
								defaultValue={[slider]}
								value={[slider]}
								max={utxos.length}
								step={1}
								onValueChange={onSliderChange}
								className="[&_[role=slider]]:size-3.5"
							/>
						</div>
						<div className="relative shrink-0" ref={sortRef}>
							<button
								onClick={() => setSortOpen(!sortOpen)}
								className="flex items-center gap-1 h-7 px-2 text-xs text-muted-foreground bg-transparent border border-border rounded-md hover:border-border transition-colors"
							>
								{sortOrder === 'biggest' ? 'Biggest' : 'Smallest'}
								<ChevronDown
									className={classNames('size-3 transition-transform', sortOpen && 'rotate-180')}
								/>
							</button>
							{sortOpen && (
								<div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-md shadow-lg overflow-hidden min-w-[120px]">
									<button
										onClick={() => handleSortChange('biggest')}
										className={classNames(
											'w-full text-left px-3 py-1.5 text-xs transition-colors',
											sortOrder === 'biggest'
												? 'text-foreground bg-accent'
												: 'text-muted-foreground hover:bg-accent hover:text-foreground'
										)}
									>
										Biggest first
									</button>
									<button
										onClick={() => handleSortChange('smallest')}
										className={classNames(
											'w-full text-left px-3 py-1.5 text-xs transition-colors',
											sortOrder === 'smallest'
												? 'text-foreground bg-accent'
												: 'text-muted-foreground hover:bg-accent hover:text-foreground'
										)}
									>
										Smallest first
									</button>
								</div>
							)}
						</div>
					</div>
				)}

				<div className="max-h-[300px] overflow-y-auto rounded-lg border border-border">
					{utxos.map((utxo: any) => {
						const isSelected = selected.has(utxo.outpoint)
						const usd = exchangeRate ? ((utxo.value / 1e8) * exchangeRate).toFixed(2) : '0'
						return (
							<div
								key={utxo.outpoint}
								onClick={() => handleSelect(utxo.outpoint)}
								className={classNames(
									'flex items-center justify-between px-3 py-2 cursor-pointer transition-colors border-b border-border/50 last:border-b-0',
									isSelected ? 'bg-violet-500/10' : 'hover:bg-accent/50'
								)}
								role="button"
								tabIndex={0}
								onKeyDown={onKeyboardClick(() => handleSelect(utxo.outpoint))}
							>
								<div className="flex items-center gap-2">
									{isSelected ? (
										<CircleCheck className="size-4 stroke-violet-500 shrink-0" />
									) : (
										<CirclePlus className="size-4 stroke-zinc-500 shrink-0" />
									)}
									<div className="flex items-center gap-1">
										<BitcoinIcon className="size-3 text-orange-500" />
										<span className="text-xs font-medium text-foreground">
											{formatSatoshis(utxo.value)}
										</span>
									</div>
								</div>
								<span className="text-[10px] text-muted-foreground">${usd}</span>
							</div>
						)
					})}
					{isLoading && (
						<div className="flex justify-center py-4">
							<div className="size-4 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
						</div>
					)}
				</div>

				<GradientButton
					className="w-full"
					disabled={!selectedUtxos.length}
					onClick={() => setStep(0)}
				>
					Select {selectedUtxos.length} UTXO{selectedUtxos.length !== 1 ? 's' : ''}
				</GradientButton>
				<button
					className="text-sm text-muted-foreground hover:text-muted-foreground transition-colors"
					onClick={handleModalClose}
				>
					Cancel
				</button>
			</div>
		)
	}

	return (
		<div className="w-full max-w-[400px] flex flex-col gap-3">
			<div>
				<h2 className="text-base font-semibold text-foreground">Create UTXOs</h2>
				<p className="text-xs text-muted-foreground mt-1">
					Your inputs will create multiple outputs. Add outputs to split transactions at the desired
					UTXO size.
				</p>
			</div>

			{/* Selected Inputs */}
			<div className="rounded-lg border border-border">
				<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
					<span className="text-xs font-semibold text-muted-foreground">Selected Inputs</span>
					<button
						onClick={() => setStep(1)}
						className="text-violet-700 dark:text-violet-400 hover:text-violet-700 dark:text-violet-300 transition-colors"
					>
						<EditIcon className="size-4" />
					</button>
				</div>
				<div className="max-h-[150px] overflow-y-auto">
					{selectedUtxos?.map((e) => {
						const usd = exchangeRate ? ((e.value / 1e8) * exchangeRate).toFixed(2) : '0'
						return (
							<div
								key={e.outpoint}
								className="flex items-center justify-between px-3 py-2 border-b border-border/50 last:border-b-0"
							>
								<div className="flex items-center gap-1">
									<BitcoinIcon className="size-3 text-orange-500" />
									<span className="text-xs font-medium text-foreground">
										{formatSatoshis(e.value)}
									</span>
								</div>
								<span className="text-[10px] text-muted-foreground">${usd}</span>
							</div>
						)
					})}
				</div>
			</div>

			{/* Outputs */}
			<div className="rounded-lg border border-border">
				<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
					<span className="text-xs font-semibold text-muted-foreground">Outputs</span>
				</div>
				<div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
					<div className="flex-1">
						<Slider
							defaultValue={[numOutputs]}
							value={[Math.min(numOutputs, 10)]}
							min={1}
							max={10}
							step={1}
							onValueChange={([v]) => setOutputs(v)}
							className="[&_[role=slider]]:size-3.5"
						/>
					</div>
					<input
						type="number"
						min={1}
						value={numOutputs}
						onChange={handleOutputsInput}
						className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
					/>
				</div>
				<div className="max-h-[150px] overflow-y-auto">
					{new Array(numOutputs).fill('').map((_, i) => {
						const usd = exchangeRate && size ? ((size / 1e8) * exchangeRate).toFixed(2) : '0'
						return (
							<div
								key={i}
								className="flex items-center justify-between px-3 py-2 border-b border-border/50 last:border-b-0"
							>
								<div className="flex items-center gap-1">
									<BitcoinIcon className="size-3 text-orange-500" />
									<span className="text-xs font-medium text-foreground">
										{size ? formatSatoshis(size) : '—'}
									</span>
								</div>
								<span className="text-[10px] text-muted-foreground">${usd}</span>
							</div>
						)
					})}
				</div>
			</div>

			{/* Network Fee */}
			<div className="rounded-lg border border-border">
				<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
					<span className="text-xs font-semibold text-muted-foreground">Network Fee</span>
					<span
						className={classNames(
							'text-[10px] font-medium',
							feeSpeed === 'Slow'
								? 'text-destructive'
								: feeSpeed === 'Medium'
								? 'text-warning'
								: 'text-success'
						)}
					>
						{feeSpeed}
					</span>
				</div>
				<div className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
					<div className="flex-1">
						<Slider
							defaultValue={[feeRate]}
							value={[feeRate]}
							min={feeMin}
							max={feeMax}
							step={1}
							onValueChange={([v]) => setFeeRate(v)}
							className="[&_[role=slider]]:size-3.5"
						/>
					</div>
					<div className="flex items-center gap-1 shrink-0">
						<input
							type="number"
							min={feeMin}
							max={1000}
							value={feeRate}
							onChange={handleFeeInput}
							className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
						/>
						<span className="text-[10px] text-muted-foreground">sat/vB</span>
					</div>
				</div>
				<div className="flex items-center justify-between px-3 py-2">
					<span className="text-xs text-muted-foreground">Total Fee</span>
					<div className="flex items-center gap-1.5">
						<BitcoinIcon className="size-3 text-orange-500" />
						<span className="text-xs font-medium text-foreground">{formatSatoshis(fee)}</span>
						<span className="text-[10px] text-muted-foreground">
							(${exchangeRate ? ((fee / 1e8) * exchangeRate).toFixed(2) : '0'})
						</span>
					</div>
				</div>
			</div>

			{/* Send to address */}
			<div className="flex flex-col gap-1.5">
				<input
					className="w-full h-9 px-3 text-xs font-medium text-foreground bg-background border border-border rounded-full placeholder:text-muted-foreground focus:outline-none focus:border-border transition-colors"
					placeholder="Send to address (optional)"
					value={sendToAddress}
					onChange={handleChange}
				/>
				<span className="text-[10px] text-muted-foreground px-1">
					If not specified, sends to your connected wallet.
				</span>
			</div>

			<p className="text-[10px] text-zinc-600 px-1">
				By clicking "Create UTXOs", you agree to the Ordinals Wallet{' '}
				<Link href="/terms" className="underline whitespace-nowrap" target="_blank">
					Terms of Service.
				</Link>
			</p>

			<GradientButton className="w-full" disabled={submitting || loading} onClick={handleSubmit}>
				{submitting ? 'Creating...' : loading ? 'Building...' : 'Create UTXOs'}
			</GradientButton>
			<button
				className="text-sm text-muted-foreground hover:text-muted-foreground transition-colors"
				onClick={() => setStep(1)}
			>
				Go Back
			</button>
		</div>
	)
}

export default UtxosCart
